import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { RouterProvider } from 'react-router';

import { createAppRouter } from '@/config/router/router';
import type { AppInitialState } from '@/domain/models/appInitialState';
import { defaultLanguage } from '@/domain/models/appLanguage';
import { setL10nLanguage } from '@/l10n/useL10n';
import { appStateStore } from '@/presentation/stores/appStateStore';
import { languageStore, useLanguage } from '@/presentation/stores/languageStore';
import { AppBackground } from '@/presentation/widgets/AppBackground';
import { AppSettingsWrapper } from '@/presentation/widgets/AppSettingsWrapper';
import { HeroProvider } from '@/presentation/widgets/heroContext';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';

/**
 * Перенос `MainApp` и `appShellBuilder` из `lib/main.dart`.
 *
 * Соответствие слоёв:
 *
 *   ProviderScope(overrides: […])  -> `initAppState` и `initLanguage`
 *                                     до первого рендера
 *   AppSettingsWrapper             -> он же (метрики экрана, тосты,
 *                                     слой загрузки вместо EasyLoading.init)
 *   MaterialApp.router             -> <RouterProvider router={router} />
 *   builder: appShellBuilder       -> AppBackground под роутером
 *   Directionality(ltr)            -> dir="ltr" на <html>
 *   locale: …                      -> setL10nLanguage + <html lang>
 *
 * `theme: ThemeData(...)` не переносится компонентом: шрифт, прозрачный
 * фон Scaffold и белый текст живут в `styles/global.css`.
 *
 * `HeroProvider` — добавка веба: во Flutter перелёты `Hero` делал сам
 * навигатор, здесь реестр прямоугольников нужен над роутером, иначе
 * `MaybeHero` при переходе от чата к источнику отдаст ребёнка без анимации.
 */

export interface AppProps {
  readonly initialState: AppInitialState;
}

function reportLanguageFailure(error: unknown): void {
  const reason = error instanceof Error ? error.message : String(error);
  console.error('[app] не удалось переключить язык интерфейса', error);
  DiagnosticsLog.instance.event(`не удалось переключить язык интерфейса: ${reason}`, {
    isError: true,
  });
}

/** `ProviderScope(overrides: […])` — состояние, собранное стартом. */
function installInitialState(initialState: AppInitialState): void {
  appStateStore.getState().initAppState(initialState.appState);
  // Язык попадает в состояние синхронно, до первого `await` внутри
  // `initLanguage`; промис дожидается только переключения переводов.
  languageStore
    .getState()
    .initLanguage(initialState.language)
    .catch(reportLanguageFailure);
}

/**
 * `Directionality(textDirection: TextDirection.ltr)` из `appShellBuilder`.
 *
 * Приложение держало LTR даже на арабском — вся вёрстка рассчитана на него,
 * и это поведение сохранено. Атрибут ставится на `<html>`, а не на обёртку:
 * тосты и слой загрузки рисуются порталами в `<body>`, и до них обёртка
 * не дотянулась бы.
 */
function useLtrDirection(): void {
  useEffect(() => {
    document.documentElement.dir = 'ltr';
  }, []);
}

/**
 * `locale: language?.locale ?? AppLanguage.defaultLanguage.locale`
 * у `MaterialApp.router`.
 *
 * Во Flutter локаль читалась из провайдера на каждой перерисовке; здесь
 * она переносится в i18next и в `<html lang>` при каждой смене языка.
 */
function useAppLocale(): void {
  const language = useLanguage();

  useEffect(() => {
    const locale = language ?? defaultLanguage;
    document.documentElement.lang = locale;
    setL10nLanguage(locale).catch(reportLanguageFailure);
  }, [language]);
}

export function App({ initialState }: AppProps): ReactElement {
  // Значения обязаны лежать в хранилищах до первого рендера страниц —
  // эффект выполнился бы уже после него. Ленивый инициализатор `useState`
  // единственное место, где React разрешает разовую работу заранее.
  useState(() => installInitialState(initialState));
  // После старта: загрузчики маршрутов читают уже заполненное хранилище.
  const [router] = useState(createAppRouter);
  useLtrDirection();
  useAppLocale();

  return (
    <HeroProvider>
      <AppSettingsWrapper>
        {/* Stack(fit: expand, [AppBackground(), child]) из appShellBuilder. */}
        <AppBackground />
        <RouterProvider router={router} />
      </AppSettingsWrapper>
    </HeroProvider>
  );
}
